import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Container,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../../../utils/api';

interface VoterData {
  id: string;
  name: string;
  username: string;
  hasVoted: boolean;
}

interface Instance {
  description: string;
}

const InstanceVotersPage = () => {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const [voters, setVoters] = useState<VoterData[]>([]);
  const [instance, setInstance] = useState<Instance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!instanceId) return;
      
      try {
        setLoading(true);
        setError(null);

        // First get the instance details
        const instanceResponse = await api.get(`/admin/instances/${instanceId}/details?fetchVoters=true`);
        console.log('Instance details:', instanceResponse.data);
        
        setInstance({
          description: instanceResponse.data.instance.description,
        });

        // Get the IDs of voters who have voted in this instance
        const votesResponse = await api.get(`/voter/fetch-votes/${instanceId}`);
        console.log('Votes response:', votesResponse.data);
        
        const votedVoterRefs = votesResponse.data.voterIds || [];
        
        if (!votedVoterRefs || votedVoterRefs.length === 0) {
          console.log('No voters found for this instance');
          setVoters([]);
          return;
        }

        // Extract the actual voter IDs from the Firestore references
        const votedVoterIds = votedVoterRefs.map((ref: any) => ref._path.segments[1]);
        console.log('Voted voter IDs:', votedVoterIds);

        // Filter the voters to only include those who have voted
        const allVoters = instanceResponse.data.voters || [];
        console.log('All voters:', allVoters);
        
        const votedVoters = allVoters
          .filter((voter: any) => votedVoterIds.includes(voter.id))
          .map((voter: any) => ({
            id: voter.id,
            name: voter.name,
            username: voter.username,
            hasVoted: true
          }));

        console.log('Filtered voters:', votedVoters);
        setVoters(votedVoters);

      } catch (err: any) {
        console.error('Error fetching data:', err);
        const errorMessage = err.response?.data?.message || err.message || 'An error occurred while fetching data';
        console.error('Error message:', errorMessage);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [instanceId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleExportCSV = () => {
    try {
      if (!instance?.description) {
        console.error('No instance description available');
        return;
      }
      
      if (!voters || voters.length === 0) {
        console.error('No voters available to export');
        return;
      }

      console.log('Preparing CSV export for voters:', voters);

      // Create CSV content
      const headers = ['Name', 'Username'];
      const rows = voters.map(voter => [
        voter.name || 'Unknown',
        voter.username || 'N/A'
      ]);

      console.log('CSV rows prepared:', rows);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create file name with instance description and current date/time
      const now = new Date();
      const timeStr = now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }).replace(':', '-');
      const dateStr = now.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');
      const sanitizedDescription = instance.description.replace(/[^a-z0-9]/gi, '_').slice(0, 30);
      const fileName = `voters_for_${sanitizedDescription}_${dateStr}_${timeStr}.csv`;

      console.log('Preparing to download file:', fileName);

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      URL.revokeObjectURL(link.href);

      console.log('CSV download completed successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError('Failed to export CSV file. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={handleBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1">
                Voters for {instance?.description || 'Loading...'}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportCSV}
            disabled={voters.length === 0}
            startIcon={<DownloadIcon />}
          >
            Export CSV
          </Button>
        </Box>

        {voters.length === 0 ? (
          <Alert severity="info">No voters have voted in this instance yet.</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Username</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {voters.map((voter) => (
                  <TableRow key={voter.id}>
                    <TableCell>{voter.name}</TableCell>
                    <TableCell>{voter.username}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Container>
  );
};

export default InstanceVotersPage;
